import {closeDialog, openConfirmDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray, isNotNilOrEmpty, noop} from 'ramda-adjunct';

import {
  usePostTiresArchiveMutation,
  usePostTireSetDeliveryNoteMutation,
  usePostTireSetLabelMutation,
  usePostTiresRestoreMutation,
  usePostTiresTransferToOrderMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, handleApiError, printPDF} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

export const TIRE_ORDER_SET_PREDEFINED_COLUMNS = [
  'orderSetId',
  'tireManufacturer',
  'tireSeason',
  'setNumber',
] as const;

export interface EditTireSetData {
  tireOrderId: string;
  setId: string;
  title: string;
}

export function useTireOrderSetActions(
  orderId: string,
  onEditTireSet: (data: EditTireSetData) => void
) {
  const [postTiresArchive] = usePostTiresArchiveMutation();
  const [postTireSetDeliveryNote] = usePostTireSetDeliveryNoteMutation();
  const [postTireSetLabel] = usePostTireSetLabelMutation();
  const [postTiresTransferToOrder] = usePostTiresTransferToOrderMutation();
  const [postTiresRestore] = usePostTiresRestoreMutation();

  const actionCallback: ActionCallback<typeof TIRE_ORDER_SET_PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const rowIds = isArray(rowId) ? rowId : [rowId];

    const setId = getColumnData<string>('orderSetId')?.value;
    const setNumber = getColumnData<string>('setNumber')?.value;
    const tireManufacturer = getColumnData<{label: string}>('tireManufacturer')?.value?.label;
    const tireSeason = getColumnData<{label: string}>('tireSeason')?.value?.label;

    if (isNil(setId)) {
      throw new Error('setId is undefined');
    }

    const transferActionCallback: ActionCallback = ({rowId}) => {
      const targetOrderId = isArray(rowId) ? head(rowId) : rowId;

      if (isNil(targetOrderId)) {
        return;
      }

      postTiresTransferToOrder({
        targetOrderId,
        body: {
          tireSets: [{tireOrderId: orderId, tireSetId: setId}],
        },
      })
        .unwrap()
        .then(() => {
          closeDialog('transferToOrder');
          refreshData();
        })
        .catch(handleApiError);
    };

    match(actionKey)
      .with('edit', 'redirectDetail', () => {
        const titleData = [tireManufacturer, tireSeason].filter(isNotNilOrEmpty);
        const title = `${setNumber} - ${titleData.join(', ')}`;

        onEditTireSet({
          tireOrderId: orderId,
          setId,
          title,
        });
      })
      .with('printLabel', () =>
        postTireSetLabel({orderId, setId, body: {wheels: rowIds.map((id) => ({id}))}})
          .unwrap()
          .then((data) => {
            const url = data?.printout?.pdfUrl;
            if (url) {
              printPDF(url);
            } else {
              showNotification.error();
            }
          })
          .catch(handleApiError)
      )
      .with('printDeliveryNote', () =>
        postTireSetDeliveryNote({orderId, setId, body: {wheels: rowIds.map((id) => ({id}))}})
          .unwrap()
          .then((data) => {
            const url = data?.printout?.pdfUrl;
            if (url) {
              printPDF(url);
            } else {
              showNotification.error();
            }
          })
          .catch(handleApiError)
      )
      .with('archive', () =>
        openConfirmDialog({
          text: i18n.t('page.tiresInventory.labels.archiveTire'),
          onConfirm: () =>
            postTiresArchive({
              orderId,
              setId,
              body: {wheels: rowIds.map((id) => ({id}))},
            })
              .unwrap()
              .then(() => {
                refreshData();
              })
              .catch(handleApiError),
        })
      )
      .with('transferToOrder', () => {
        openDialog(
          <DmsDataGrid
            gridCode="tire-set-transfer"
            autoHeight
            actionCallback={transferActionCallback}
            data-testid={testIds.tiresInventory.tireOrderDetail('transferToOrder')}
          />,
          {
            id: 'transferToOrder',
            title: i18n.t('page.tiresInventory.labels.transferToOrder'),
            size: 'large',
          }
        );
      })
      .with('restoreWheel', () => {
        postTiresRestore({
          orderId,
          setId,
          body: {wheels: rowIds.map((id) => ({id}))},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .otherwise(noop);
  };

  return actionCallback;
}
